import dayjs from "dayjs";
import { KeyboardEvent, ReactNode, useMemo, useRef, useState } from "react";
import {
  FieldError,
  FieldPathByValue,
  PathValue,
  UseFormRegisterReturn,
  Validate,
  get,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";
import { HiOutlineXMark } from "react-icons/hi2";
import { StatusSwitch } from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/controls";
import {
  cardClassName,
  fieldLabelClassName,
  textInputClassName,
} from "@/app/components/routes/auth_routes/QuickCommerce/HomeTileConfig/interface";
import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { SelectMenuItems } from "@/app/components/ui/interface";
import { GIFT_FORM_DATE_FORMAT } from "../formUtils";
import { IGiftFormFields } from "../interface";

export const errorBorderClassName = (hasError: boolean) => (hasError ? "border-rose-400" : "");

export const FormField = ({
  label,
  htmlFor,
  error,
  hint,
  required = false,
  children,
}: {
  label: string;
  htmlFor?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}) => (
  <div>
    {/* An empty label renders nothing, for a control placed beside another field under one label. */}
    {label && (
      <label className={fieldLabelClassName} htmlFor={htmlFor}>
        {label}
        {required && <span className="text-rose-500">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <p className="mt-1 text-[11px] text-rose-600">{error}</p>
    ) : (
      hint && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>
    )}
  </div>
);

export const SectionCard = ({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <div className={cardClassName}>
    <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
      <div className="min-w-0">
        <span className="text-sm font-semibold text-gray-900">{title}</span>
        {description && <p className="text-xs text-slate-500">{description}</p>}
      </div>
      {action && <div className="ml-auto flex items-center gap-2.5">{action}</div>}
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

/** A labelled on/off field the same height as a text input, for use in a row of inputs. */
export const InlineSwitchField = ({
  label,
  checked,
  onChange,
  onText = "Yes",
  offText = "No",
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  onText?: string;
  offText?: string;
}) => (
  <FormField label={label}>
    <div className="flex h-9 items-center justify-between rounded-lg border border-slate-200 bg-white px-3 shadow-sm">
      <span className="text-sm text-slate-700">{checked ? onText : offText}</span>
      <StatusSwitch checked={checked} onRequestChange={onChange} label={label} />
    </div>
  </FormField>
);

export const SwitchField = ({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  hint?: string;
}) => (
  <div className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2.5">
    <div>
      <p className="text-xs font-medium text-slate-700">{label}</p>
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
    </div>
    <StatusSwitch checked={checked} onRequestChange={onChange} label={label} />
  </div>
);

// SearchableSelectMenu only turns red for the error types listed here; the message itself is shown by FormField.
const SELECT_ERROR_TYPES = [
  { type: "required", message: "" },
  { type: "validate", message: "" },
];

const selectMenuStyleProps = {
  showDropdownIcon: true,
  showTypedErrors: false,
  // 34px input + 1px container border on each side = 36px, the same height as textInputClassName (h-9).
  className: "!h-[34px] !py-0 !text-sm !text-slate-700",
  inputContainerClassName: "bg-white !border-slate-200 shadow-sm",
  listBoxClassName: "w-full",
  containerClassName: "w-full",
} as const;

type GiftSelectName = FieldPathByValue<IGiftFormFields, string>;

/** Single-value dropdown bound to a form field, built on the shared SearchableSelectMenu. */
export const GiftSelectField = ({
  name,
  label,
  items,
  placeholder = "Select",
  hint,
  required = false,
  validate,
  isLoading = false,
  withReset = false,
}: {
  name: GiftSelectName;
  label: string;
  items: SelectMenuItems;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  /** Use instead of `required` when the field is only required conditionally; `required` then only shows the asterisk. */
  validate?: Validate<string, IGiftFormFields>;
  isLoading?: boolean;
  withReset?: boolean;
}) => {
  const { control, register, setValue } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control, name });
  const value = useWatch({ control, name }) as string;
  const fieldError: FieldError | undefined = get(errors, name);
  const selectedItem = useMemo(() => items.find((item) => item.id === value), [items, value]);

  return (
    <FormField label={label} required={required} error={fieldError?.message} hint={hint}>
      <SearchableSelectMenu
        {...selectMenuStyleProps}
        register={register(name, { required: required && !validate && `${label} is required.`, validate })}
        fieldError={fieldError}
        errorMessages={SELECT_ERROR_TYPES}
        selectItems={items}
        defaultSelected={selectedItem}
        placeholder={isLoading ? "Loading…" : placeholder}
        disabled={isLoading}
        withReset={withReset}
        onSelectItem={(item) =>
          setValue(name, (item?.id ?? "") as PathValue<IGiftFormFields, GiftSelectName>, {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
      />
    </FormField>
  );
};

type GiftDateName = "start_date" | "end_date";

/**
 * Date picker bound to a "YYYY-MM-DD" form field, built on the shared PWLCalendar.
 * Shows the date as yy/MM/dd; `minDate` ("YYYY-MM-DD") blocks earlier days in the calendar.
 */
export const GiftDateField = ({
  name,
  label,
  hint,
  minDate,
  validate,
}: {
  name: GiftDateName;
  label: string;
  hint?: string;
  minDate?: string;
  validate?: Validate<string, IGiftFormFields>;
}) => {
  const { control, register, setValue } = useFormContext<IGiftFormFields>();
  const { errors } = useFormState({ control, name });
  const value = useWatch({ control, name });
  const fieldError = errors[name];

  return (
    <FormField label={label} required error={fieldError?.message} hint={hint}>
      <PWLCalendarComponent
        register={register(name, { required: `${label} is required.`, validate })}
        fieldError={fieldError}
        errorMessages={SELECT_ERROR_TYPES}
        defaultSelectedDate={value || undefined}
        displayDateFormat="yy/MM/dd"
        disableDatesBeforeDate={minDate || undefined}
        showFutureYears
        className="h-9 !text-sm !text-slate-700 shadow-sm"
        onSelectDate={(date) =>
          setValue(name, date ? dayjs(date).format(GIFT_FORM_DATE_FORMAT) : "", {
            shouldDirty: true,
            shouldValidate: true,
          })
        }
      />
    </FormField>
  );
};

/** Removable chips for a multi-value field; `getLabel` maps a stored value (e.g. an id) to what is shown. */
const ChipList = ({
  values,
  onChange,
  getLabel = (item) => item,
}: {
  values: string[];
  onChange: (next: string[]) => void;
  getLabel?: (item: string) => string;
}) =>
  values.length > 0 ? (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {values.map((item) => (
        <span
          key={item}
          title={item}
          className="inline-flex items-center gap-1 rounded-full border border-[#193A6A]/20 bg-[#EEF5FF] py-0.5 pl-2.5 pr-1 text-[11px] font-medium text-[#193A6A]"
        >
          {getLabel(item)}
          <button
            type="button"
            onClick={() => onChange(values.filter((selected) => selected !== item))}
            className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-rose-100 hover:text-rose-600"
            aria-label={`Remove ${getLabel(item)}`}
          >
            <HiOutlineXMark className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </span>
      ))}
    </div>
  ) : null;

/** Orders options so titles starting with the query come first, then titles containing it, each A–Z. */
const rankByQuery = (items: SelectMenuItems, query: string): SelectMenuItems => {
  const needle = query.trim().toLowerCase().replace(/\s+/g, "");
  const rank = (title: string) => {
    if (!needle) return 0;
    const haystack = title.toLowerCase().replace(/\s+/g, "");
    if (haystack.startsWith(needle)) return 0;
    return haystack.includes(needle) ? 1 : 2;
  };
  return [...items].sort((a, b) => rank(a.title) - rank(b.title) || a.title.localeCompare(b.title));
};

/**
 * Multi-select over a known list (e.g. clinics): type a name to search, pick to add a chip.
 * Stores item ids; ids not in `items` (saved earlier, since removed) still show as chips with the raw id.
 */
export const ChipSearchSelect = ({
  id,
  items,
  value,
  onChange,
  placeholder,
  blockedValues = [],
  names = {},
  onPick,
  isLoading = false,
  hasError = false,
}: {
  id: string;
  items: SelectMenuItems;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  blockedValues?: string[];
  /** Names for ids not in `items` (e.g. from the saved gift). */
  names?: Record<string, string>;
  /** Called with the picked item, e.g. to remember its name for the payload. */
  onPick?: (item: { id: string; title: string }) => void;
  isLoading?: boolean;
  hasError?: boolean;
}) => {
  const [query, setQuery] = useState<string>("");
  const titleById = useMemo(() => new Map(items.map((item) => [item.id, item.title])), [items]);
  const availableItems = useMemo(
    () =>
      rankByQuery(
        items.filter((item) => !value.includes(item.id) && !blockedValues.includes(item.id)),
        query,
      ),
    [items, value, blockedValues, query],
  );

  return (
    <div>
      {/* Remount after every change so the search box clears once an item is added. */}
      <SearchableSelectMenu
        key={value.join("|")}
        {...selectMenuStyleProps}
        register={{ name: id } as UseFormRegisterReturn}
        fieldError={hasError ? { type: "validate" } : undefined}
        errorMessages={SELECT_ERROR_TYPES}
        selectItems={availableItems}
        placeholder={isLoading ? "Loading…" : placeholder}
        disabled={isLoading}
        showDropdownWhenNoData
        dropdownNoDataContent={<span className="text-[10px] sm:text-xs">No match</span>}
        onInputChange={setQuery}
        onSelectItem={(item) => {
          setQuery("");
          if (!item || value.includes(item.id)) return;
          onPick?.(item);
          onChange([...value, item.id]);
        }}
      />
      <ChipList values={value} onChange={onChange} getLabel={(item) => titleById.get(item) || names[item] || item} />
    </div>
  );
};

// SearchableSelectMenu clears its options whenever `selectItems` changes identity, so an inline `[]` would wipe
// API results on every parent re-render (the search hook's own state update causes one). Keep it stable.
const NO_STATIC_ITEMS: SelectMenuItems = [];

const API_SEARCH_MIN_LENGTH = 3;
const API_SEARCH_DEBOUNCE_MS = 300;

/**
 * Multi-select backed by a search API (e.g. products): results load as the user types (3+ letters).
 * Stores ids; `names` gives chip labels and `onPick` lets the caller remember a picked item's name.
 */
export const ChipApiSearchSelect = ({
  id,
  value,
  onChange,
  search,
  placeholder,
  blockedValues = [],
  names = {},
  onPick,
  hasError = false,
}: {
  id: string;
  value: string[];
  onChange: (next: string[]) => void;
  search: (query: string) => Promise<SelectMenuItems>;
  placeholder: string;
  blockedValues?: string[];
  names?: Record<string, string>;
  onPick?: (item: { id: string; title: string }) => void;
  hasError?: boolean;
}) => {
  const latestQueryRef = useRef<string>("");

  // SearchableSelectMenu calls this on every keystroke past 3 letters; wait for typing to pause and
  // drop answers for older queries. Already chosen or blocked ids are left out of the results.
  const apiCallback = async (query: string): Promise<SelectMenuItems> => {
    latestQueryRef.current = query;
    await new Promise((resolve) => setTimeout(resolve, API_SEARCH_DEBOUNCE_MS));
    if (latestQueryRef.current !== query) return [];
    const results = await search(query);
    if (latestQueryRef.current !== query) return [];
    return results.filter((item) => !value.includes(item.id) && !blockedValues.includes(item.id));
  };

  return (
    <div>
      {/* Remount after every change so the search box clears once an item is added. */}
      <SearchableSelectMenu
        key={value.join("|")}
        {...selectMenuStyleProps}
        register={{ name: id } as UseFormRegisterReturn}
        fieldError={hasError ? { type: "validate" } : undefined}
        errorMessages={SELECT_ERROR_TYPES}
        selectItems={NO_STATIC_ITEMS}
        placeholder={placeholder}
        withAPISearch={{ apiCallback, reducerTitleKey: "title", reducerIdKey: "id" }}
        onSelectItem={(item) => {
          if (!item || value.includes(item.id)) return;
          onPick?.(item);
          onChange([...value, item.id]);
        }}
      />
      {value.length === 0 && (
        <p className="mt-1 text-[11px] text-slate-400">Type at least {API_SEARCH_MIN_LENGTH} letters to search.</p>
      )}
      <ChipList values={value} onChange={onChange} getLabel={(item) => names[item] || item} />
    </div>
  );
};

/**
 * Free-text multi-value input rendered as removable chips. Enter, comma or the Add button adds the typed
 * value as one chip, spaces included ("pai conversion"); pasting "a, b" or one value per line adds each one.
 * Values found in `blockedValues` are refused with an inline message.
 */
export const ChipTextInput = ({
  id,
  value,
  onChange,
  placeholder,
  blockedValues = [],
  blockedMessage = "Already used elsewhere.",
  getLabel,
  hasError = false,
}: {
  id: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  blockedValues?: string[];
  blockedMessage?: string;
  /** Text shown on a chip for a value; defaults to the value itself. */
  getLabel?: (item: string) => string;
  hasError?: boolean;
}) => {
  const [draft, setDraft] = useState<string>("");
  const [notice, setNotice] = useState<string>("");

  const addValues = (raw: string) => {
    const entries = raw
      // Commas and line breaks separate values; spaces belong to the value itself.
      .split(/[,\n\r]+/)
      .map((entry) => entry.trim().replace(/\s+/g, " "))
      .filter(Boolean);
    if (!entries.length) return;
    const blocked = entries.filter((entry) => blockedValues.includes(entry));
    const next = Array.from(new Set([...value, ...entries.filter((entry) => !blockedValues.includes(entry))]));
    if (next.length !== value.length) onChange(next);
    setNotice(blocked.length ? `${blocked.join(", ")}: ${blockedMessage}` : "");
    setDraft("");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === ",") {
      // Enter must not submit the gift form.
      event.preventDefault();
      addValues(draft);
    } else if (event.key === "Backspace" && !draft && value.length) {
      onChange(value.slice(0, -1));
    }
  };

  return (
    <div>
      <div className="flex gap-2">
        <input
          id={id}
          type="text"
          value={draft}
          placeholder={placeholder}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => addValues(draft)}
          onPaste={(event) => {
            const pasted = event.clipboardData.getData("text");
            if (/[,\n\r]/.test(pasted.trim())) {
              event.preventDefault();
              addValues(`${draft}${pasted}`);
            }
          }}
          className={`${textInputClassName} ${errorBorderClassName(hasError)}`}
        />
        <button
          type="button"
          onClick={() => addValues(draft)}
          disabled={!draft.trim()}
          className="shrink-0 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-[#193A6A] transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Add
        </button>
      </div>
      {notice && <p className="mt-1 text-[11px] text-amber-600">{notice}</p>}
      <ChipList values={value} onChange={onChange} getLabel={getLabel} />
    </div>
  );
};
